import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { Button, Card, CardBody, CardTitle } from "reactstrap";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import OpenItemsModal from "../open-items-modal";
import RecommendedActionItem from "../recommended-action-item";

const NextSteps = ({ t, softwareId, recommendedActions = {} }) => {
    const [showAllItemsModal, setShowAllItemsModal] = useState(false);
    const items = recommendedActions.items || [];
    const summary = recommendedActions.summary || {};
    const shownCount = items.length;
    const totalCount = Number.isFinite(summary.total) ? summary.total : shownCount;

    const allItemsQuery = useQuery({
        queryKey: ["data-protection-compliance-recommended-actions-all", softwareId],
        queryFn: async () => {
            const service = DataProtectionSoftwareService.getInstance();

            return await service.fetchComplianceRecommendedActions(softwareId);
        },
        enabled: showAllItemsModal && Boolean(softwareId),
        refetchOnWindowFocus: false
    });

    const toggleAllItemsModal = useCallback(() => {
        setShowAllItemsModal((current) => !current);
    }, []);

    return (
        <Card className="overview-next-steps mb-4">
            <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                    <div>
                        <CardTitle className="overview-section-title mb-1">
                            {t("Next Steps")}
                        </CardTitle>
                        {totalCount > 0 && (
                            <div className="small text-muted">
                                {t("{{shown}} of {{total}} open items that require your attention", {
                                    shown: shownCount,
                                    total: totalCount
                                })}
                            </div>
                        )}
                    </div>
                    {totalCount > 0 && (
                        <Button
                            color="link"
                            className="p-0 small text-decoration-none"
                            onClick={toggleAllItemsModal}
                        >
                            {t("View all {{count}} items", { count: totalCount })}
                        </Button>
                    )}
                </div>

                {items.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        {t("No immediate actions require your attention.")}
                    </div>
                ) : (
                    <div>
                        {items.map((row) => (
                            <RecommendedActionItem
                                key={row.id}
                                row={row}
                                assetId={softwareId}
                                t={t}
                                showModule
                            />
                        ))}
                    </div>
                )}
            </CardBody>
            <OpenItemsModal
                assetId={softwareId}
                isOpen={showAllItemsModal}
                toggle={toggleAllItemsModal}
                itemsQuery={allItemsQuery}
            />
        </Card>
    );
};

export default withNamespaces()(NextSteps);
